import { inject, Injectable } from '@angular/core';
import { Timestamp, where } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ConversationMessage,
  ConversationParticipant,
  ConversationThread,
  CreateConversationInput,
} from 'src/app/messages/workspace/messages-workspace.model';
import { User } from 'src/app/models/user.model';
import { FirestoreService } from 'src/app/services/firestore.service';
import { convertTimestamp } from 'src/app/utility/time';

import {isConversationUnread} from '../messages/workspace/conversation-read-state';

const threadsCollection = 'message-conversations';

@Injectable({
  providedIn: 'root',
})
export class ConversationMessagesService {
  private firestore = inject(FirestoreService);

  private getMillis(value?: Date | Timestamp | null) {
    return convertTimestamp(value)?.getTime() ?? 0;
  }

  private toParticipant(user: User): ConversationParticipant {
    return {
      guid: user.guid,
      name: user.displayName,
      role: user.role ?? null,
    };
  }

  private normalizeMessage(item: ConversationMessage): ConversationMessage {
    return {
      ...item,
      createdAt: convertTimestamp(item.createdAt),
    };
  }

  private normalizeThread(item: ConversationThread): ConversationThread {
    const lastReadAtBy = Object.entries(item.lastReadAtBy ?? {}).reduce<Record<string, Date>>((acc, [key, value]) => {
      acc[key] = convertTimestamp(value);
      return acc;
    }, {});

    return {
      ...item,
      createdAt: convertTimestamp(item.createdAt),
      updatedAt: convertTimestamp(item.updatedAt),
      lastMessage: item.lastMessage ? {
        ...item.lastMessage,
        createdAt: convertTimestamp(item.lastMessage.createdAt),
      } : null,
      lastReadAtBy,
    };
  }

  private async saveInitialMessage(threadId: string, message: ConversationMessage) {
    return this.firestore.save(`${threadsCollection}/${threadId}/messages`, message);
  }

  getThreadsForUser(userGuid: string): Observable<ConversationThread[]> {
    return this.firestore.getList<ConversationThread>(
      threadsCollection,
      undefined,
      [where('participantIds', 'array-contains', userGuid)],
    ).pipe(
      map((items) => items.map((item) => this.normalizeThread(item))),
    );
  }

  getUnreadCountForUser(actor: string, contractor: string): Observable<number> {
    return this.getThreadsForUser(actor).pipe(map(threads => threads.filter(thread =>
      thread.contractor === contractor && isConversationUnread(thread, actor)).length));
  }

  getMessages(threadId: string): Observable<ConversationMessage[]> {
    return this.firestore.getList<ConversationMessage>(`${threadsCollection}/${threadId}/messages`).pipe(
      map((items) => items.map((item) => this.normalizeMessage(item))),
      map((items) => items.sort((a, b) => this.getMillis(a.createdAt) - this.getMillis(b.createdAt))),
    );
  }

  async createConversation(input: CreateConversationInput): Promise<string[]> {
    const now = Timestamp.now();
    const title = input.title?.trim() ?? '';
    const content = input.content.trim();
    const recipients = input.recipients.filter((recipient) => recipient?.guid && recipient.guid !== input.sender.guid);
    const isGrouped = recipients.length > 1;
    const groupId = isGrouped ? `${input.sender.guid}-${Date.now()}-${recipients.length}` : null;
    const groupLabel = isGrouped ? input.audienceLabel?.trim() || title || null : null;
    const senderParticipant = this.toParticipant(input.sender);
    const savedThreadIds: string[] = [];

    for (const recipient of recipients) {
      const participants = [senderParticipant, this.toParticipant(recipient)];
      const thread: ConversationThread = {
        contractor: input.contractor,
        title,
        type: input.type,
        context: input.context ?? null,
        createdById: input.sender.guid,
        createdByName: input.sender.displayName,
        createdByRole: input.sender.role,
        participantIds: participants.map((participant) => participant.guid),
        participants,
        archivedBy: [],
        lastReadAtBy: {
          [input.sender.guid]: now,
        },
        lastMessage: {
          senderId: input.sender.guid,
          senderName: input.sender.displayName,
          content,
          createdAt: now,
        },
        groupId,
        groupLabel,
        recipientId: recipient.guid,
        recipientName: recipient.displayName,
        createdAt: now,
        updatedAt: now,
      };
      const savedThreadRef = await this.firestore.save(threadsCollection, thread);
      const threadId = savedThreadRef?.id;
      if (!threadId) {
        continue;
      }

      const message: ConversationMessage = {
        threadId,
        senderId: input.sender.guid,
        senderName: input.sender.displayName,
        senderRole: input.sender.role,
        type: input.type,
        content,
        createdAt: now,
      };

      await this.saveInitialMessage(threadId, message);
      savedThreadIds.push(threadId);
    }

    return savedThreadIds;
  }

  async sendMessage(thread: ConversationThread, sender: User, content: string) {
    const now = Timestamp.now();
    const body = content.trim();
    const message: ConversationMessage = {
      threadId: thread.guid,
      senderId: sender.guid,
      senderName: sender.displayName,
      senderRole: sender.role,
      type: thread.type,
      content: body,
      createdAt: now,
    };

    await this.saveInitialMessage(thread.guid, message);
    await this.firestore.updateOnly(threadsCollection, thread.guid, {
      updatedAt: now,
      lastMessage: {
        senderId: sender.guid,
        senderName: sender.displayName,
        content: body,
        createdAt: now,
      },
      [`lastReadAtBy.${sender.guid}`]: now,
    });
  }

  async markAsRead(thread: ConversationThread, userGuid: string) {
    return this.firestore.updateOnly(threadsCollection, thread.guid, {
      [`lastReadAtBy.${userGuid}`]: Timestamp.now(),
    });
  }

  async setArchived(thread: ConversationThread, userGuid: string, archived: boolean) {
    const archivedBy = new Set(thread.archivedBy ?? []);
    if (archived) {
      archivedBy.add(userGuid);
    } else {
      archivedBy.delete(userGuid);
    }

    return this.firestore.updateOnly(threadsCollection, thread.guid, {
      archivedBy: [...archivedBy],
    });
  }
}
